/**
 * DatabricksForm — Databricks SQL warehouse connection form (RHF-based, add-only).
 *
 * Two auth paths: a personal access token (PAT) or an OAuth M2M service principal
 * (client id + secret). The workspace host is read-only — it is the bucket's host dim,
 * typed in step 2 or taken from the RudderStack destination — while the HTTP path is
 * editable. The catalog is read-only on the same grounds *when the account carries one*: it is
 * the bucket's db dim, and the server re-derives the account key from it
 * (`assertDatabricksConfigMatchesBucketDims`), so editing it here could only produce a save the
 * server rejects. An account created without a catalog (`dbDimOptional` — the workspace default
 * applies at query time) has no dim to freeze, so the field stays editable and empty there.
 *
 * The "Account details" disclosure starts **open when no HTTP path was prefilled**. The
 * HTTP path names the SQL warehouse a query runs on: Databricks' compute identity, and a
 * third thing beyond the two bucket dims, so a hand-typed bucket (phase 1.5, AI-1370)
 * holds no value for it. It is also *required*, so leaving it collapsed made Test a silent
 * no-op — validation failed on a field the customer could not see (AI-1370).
 *
 * Pure field collector: it owns RHF state and exposes `validate()` + `getValues()`
 * via an imperative handle; Test/Save are driven by the parent (the drawer header).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { isSafeSqlIdentifier } from '@server/redshift';
import type { DatabricksConnectionConfig } from '@server/types';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/_shared/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { ComboboxField } from '@/_shared/components/ui/combobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';

import {
  type FormDraft,
  useDraftDefaults,
  useResetOnChange,
  useWarehouseFormHandle,
  type WarehouseFormHandle,
} from '../shared/formHandle';
import { FormSection } from '../shared/FormSection';
import { HiddenFieldErrors } from '../shared/HiddenFieldErrors';

type DatabricksAuthType = 'pat' | 'oauth_m2m';

interface DatabricksFormProps {
  /** Values typed before the add flow remounted the form for a new account (see `useDraftDefaults`). */
  draft?: FormDraft;
  /** The add flow names the account above the form (ACT2-1031), so its identity fields are not repeated here. */
  hideIdentity?: boolean;
  /** Where the HTTP path suggestion popup portals — the drawer's sheet content, so it stays clickable. */
  portalContainer?: React.RefObject<HTMLElement | null>;
  /** Prefilled config from the hook — account details (host/httpPath/catalog) from the RudderStack destination. */
  initialConfig?: DatabricksConnectionConfig;
  /** Distinct SQL-warehouse HTTP paths seen across the bucket's destinations (dropdown suggestions). */
  availableWarehouses?: string[];
  /** Fired whenever a field changes, so the parent can reset a stale test result. */
  onChange?: () => void;
}

const COMPUTE_WAREHOUSE_HINT = 'The SQL warehouse Lookout queries run on.';

interface DatabricksFormValues {
  authType: DatabricksAuthType;
  host: string;
  httpPath: string;
  catalog: string;
  schema: string;
  token: string;
  oauthClientId: string;
  oauthClientSecret: string;
}

const SchemaModel = z
  .object({
    authType: z.enum(['pat', 'oauth_m2m']),
    host: z.string().trim().min(1, 'Workspace host missing — re-sync this destination from RudderStack'),
    // Editable, and the one account-detail field a bucket never carries — so it is phrased as
    // something to enter, not as a destination to go re-sync.
    httpPath: z.string().trim().min(1, 'HTTP path is required'),
    catalog: z.string().refine((v) => !v.trim() || isSafeSqlIdentifier(v.trim()), 'Invalid catalog name'),
    schema: z.string().refine((v) => !v.trim() || isSafeSqlIdentifier(v.trim()), 'Invalid schema name'),
    token: z.string(),
    oauthClientId: z.string(),
    oauthClientSecret: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.authType === 'pat') {
      if (!values.token.trim()) {
        ctx.addIssue({ code: 'custom', path: ['token'], message: 'Personal access token is required' });
      }
      return;
    }
    if (!values.oauthClientId.trim()) {
      ctx.addIssue({ code: 'custom', path: ['oauthClientId'], message: 'OAuth client ID is required' });
    }
    if (!values.oauthClientSecret.trim()) {
      ctx.addIssue({ code: 'custom', path: ['oauthClientSecret'], message: 'OAuth client secret is required' });
    }
  });

export const DatabricksForm = React.forwardRef<WarehouseFormHandle, DatabricksFormProps>(function DatabricksForm(
  { draft, initialConfig, availableWarehouses = [], onChange, hideIdentity = false, portalContainer },
  ref,
): React.JSX.Element {
  const { base, defaults } = useDraftDefaults<DatabricksFormValues>(
    {
      authType: initialConfig?.authType ?? 'pat',
      host: initialConfig?.host ?? '',
      httpPath: initialConfig?.httpPath ?? '',
      catalog: initialConfig?.catalog ?? '',
      schema: initialConfig?.schema ?? '',
      token: '',
      oauthClientId: initialConfig?.oauthClientId ?? '',
      oauthClientSecret: '',
    },
    draft,
  );
  const form = useForm<DatabricksFormValues>({
    resolver: zodResolver(SchemaModel),
    mode: 'onTouched',
    defaultValues: defaults,
  });

  const authType = form.watch('authType');
  const httpPathOptions = React.useMemo(
    () => availableWarehouses.map((wh) => ({ value: wh, label: wh })),
    [availableWarehouses],
  );
  // Frozen only when the account supplied the catalog dim; see the header note.
  const catalogFromAccount = Boolean(initialConfig?.catalog?.trim());
  // Decided once, from the initial prefill, so a customer who collapses it afterwards keeps it
  // collapsed. Open when the HTTP path is still missing — it is required, and hiding it is what
  // made Test a silent no-op on a field nobody could see.
  const [accountDetailsOpen] = React.useState(() => !initialConfig?.httpPath?.trim());

  const buildConfig = React.useCallback((values: DatabricksFormValues): DatabricksConnectionConfig => {
    const config: DatabricksConnectionConfig = {
      host: values.host.trim(),
      httpPath: values.httpPath.trim(),
      authType: values.authType,
    };
    if (values.catalog.trim()) config.catalog = values.catalog.trim();
    if (values.schema.trim()) config.schema = values.schema.trim();
    if (values.authType === 'pat') {
      if (values.token.trim()) config.token = values.token.trim();
    } else {
      if (values.oauthClientId.trim()) config.oauthClientId = values.oauthClientId.trim();
      if (values.oauthClientSecret.trim()) config.oauthClientSecret = values.oauthClientSecret.trim();
    }
    return config;
  }, []);

  useWarehouseFormHandle(ref, form, buildConfig, base);
  // A passing test no longer reflects edited values — let the parent reset it.
  useResetOnChange(form, onChange);

  // Type or pick: the destinations' HTTP paths are suggestions, never the only choice.
  const httpPathField = (
    <FormField
      control={form.control}
      name="httpPath"
      render={({ field }) => (
        <FormSection label="HTTP Path" description={COMPUTE_WAREHOUSE_HINT}>
          {httpPathOptions.length > 0 ? (
            <ComboboxField
              mode="single"
              allowCustomValue
              aria-label="HTTP Path"
              options={httpPathOptions}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="/sql/1.0/warehouses/abc123"
              size="lg"
              className="w-full"
              portalContainer={portalContainer}
            />
          ) : (
            <Input {...field} placeholder="/sql/1.0/warehouses/abc123" />
          )}
        </FormSection>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="grid gap-5" noValidate>
        {hideIdentity && (
          <HiddenFieldErrors
            control={form.control}
            fields={[
              { name: 'host', label: 'Workspace host' },
              { name: 'catalog', label: 'Catalog' },
            ]}
          />
        )}
        {/* On the add page the host lives above the form, so the HTTP path is all that is left of
            "Account details": show it directly rather than behind a disclosure. */}
        {hideIdentity ? (
          httpPathField
        ) : (
          <Collapsible className="grid gap-4" defaultOpen={accountDetailsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="text"
                className="group justify-self-start text-sm font-medium text-muted-foreground hover:text-foreground"
                suffixIcon={<ChevronDown className="transition-transform group-data-[state=open]:rotate-180" />}
              >
                Account details
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="grid gap-4">
              <FormField
                control={form.control}
                name="host"
                render={({ field }) => (
                  <FormSection label="Workspace Host">
                    <Input {...field} readOnly aria-readonly placeholder="dbc-abc123.cloud.databricks.com" />
                  </FormSection>
                )}
              />
              {httpPathField}
            </CollapsibleContent>
          </Collapsible>
        )}

        <FormField
          control={form.control}
          name="authType"
          render={({ field }) => (
            <FormItem className="rounded-xl border border-grey-50 bg-grey-25 p-4">
              <FormLabel>Authentication type</FormLabel>
              <FormDescription>How Lookout authenticates to your Databricks SQL warehouse.</FormDescription>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="pat">Personal access token (PAT)</SelectItem>
                  <SelectItem value="oauth_m2m">OAuth M2M service principal</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {authType === 'pat' ? (
          <FormField
            control={form.control}
            name="token"
            render={({ field }) => (
              <FormSection label="Personal Access Token" description="Stored encrypted.">
                <Input {...field} type="password" autoComplete="off" placeholder="Enter a personal access token" />
              </FormSection>
            )}
          />
        ) : (
          <>
            <FormField
              control={form.control}
              name="oauthClientId"
              render={({ field }) => (
                <FormSection label="OAuth Client ID" description="The service principal's application ID.">
                  <Input {...field} placeholder="Service principal application ID" />
                </FormSection>
              )}
            />
            <FormField
              control={form.control}
              name="oauthClientSecret"
              render={({ field }) => (
                <FormSection label="OAuth Client Secret" description="Stored encrypted.">
                  <Input {...field} type="password" autoComplete="off" placeholder="Enter a client secret" />
                </FormSection>
              )}
            />
          </>
        )}

        {!hideIdentity && (
          <FormField
            control={form.control}
            name="catalog"
            render={({ field }) => (
              <FormSection
                label="Catalog"
                optional
                description={
                  catalogFromAccount ? 'Set when the account was added.' : "Empty means the workspace's default."
                }
              >
                <Input
                  {...field}
                  readOnly={catalogFromAccount}
                  aria-readonly={catalogFromAccount || undefined}
                  placeholder="main"
                />
              </FormSection>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="schema"
          render={({ field }) => (
            <FormSection
              label="Schema"
              optional
              description="Default schema for queries. Empty means the catalog's default."
            >
              <Input {...field} placeholder="default" />
            </FormSection>
          )}
        />
      </form>
    </Form>
  );
});
